import { defaultNotificationChannelEnabled, type NotificationPreference, type UserPreferences } from '@terros/common'

export type NotificationPreferenceKey = keyof UserPreferences
export type NotificationChannel = keyof NotificationPreference

type NotificationPreferenceVisibility = 'always' | 'tantalim' | 'recording'

export type NotificationPreferenceOption = {
  key: NotificationPreferenceKey
  label: string
  allowsSingleUser: boolean
  visibility: NotificationPreferenceVisibility
}

export type NotificationPreferenceAccess = {
  isTantalim: boolean
  hasRecording: boolean
  isSingleUser: boolean
}

type ChannelInput = {
  preferences: UserPreferences | undefined
  key: NotificationPreferenceKey
  channel: NotificationChannel
}

type SetChannelInput = ChannelInput & { value: boolean }

const option = (
  key: NotificationPreferenceKey,
  label: string,
  config: Partial<Pick<NotificationPreferenceOption, 'allowsSingleUser' | 'visibility'>> = {}
): NotificationPreferenceOption => ({
  key,
  label,
  allowsSingleUser: config.allowsSingleUser ?? false,
  visibility: config.visibility ?? 'always',
})

/** Rows, labels, order, and gating mirror the iOS CommunicationPreferencesScreen. */
export const NOTIFICATION_PREFERENCE_OPTIONS: readonly NotificationPreferenceOption[] = [
  option('all', 'All Notifications', { allowsSingleUser: true }),
  option('demoSetupComplete', 'Demo Setup Complete', { visibility: 'tantalim' }),
  option('demoSetupFailed', 'Demo Setup Failed', { visibility: 'tantalim' }),
  option('dispatcherAssignment', 'Dispatcher Assignment'),
  option('closeEvents', 'Close Events'),
  option('shadowEvents', 'Shadow Events'),
  option('submitPlan', 'Submit Plan'),
  option('planSubmitted', 'Plan Submitted'),
  option('planReviewed', 'Plan Reviewed'),
  option('interviews', 'Interviews'),
  option('tasks', 'Tasks'),
  option('messages', 'Messages'),
  option('areaAssignment', 'Area Assignment'),
  option('teamAreaAssignment', 'Team Area Assignment'),
  option('game', 'Competitions'),
  option('recordingScored', 'Recording Scored', { allowsSingleUser: true, visibility: 'recording' }),
  option('recordingAssigned', 'Recording Assigned'),
  option('recordingFeedbackAdded', 'Recording Feedback Added'),
  option('recordingEventSuggestion', 'Recording Event Suggestions'),
  option('cardViewed', 'Card Opened', { allowsSingleUser: true }),
  option('cardContactSaved', 'Contact Saved', { allowsSingleUser: true }),
]

const isOptionVisible = (
  visibility: NotificationPreferenceVisibility,
  access: NotificationPreferenceAccess
): boolean => {
  switch (visibility) {
    case 'always':
      return true
    case 'tantalim':
      return access.isTantalim
    case 'recording':
      return access.hasRecording
  }
}

export function getVisibleNotificationPreferenceOptions(
  access: NotificationPreferenceAccess
): NotificationPreferenceOption[] {
  return NOTIFICATION_PREFERENCE_OPTIONS.filter(
    (item) => isOptionVisible(item.visibility, access) && (!access.isSingleUser || item.allowsSingleUser)
  )
}

/** An unsaved channel shows push as on and email as off. */
export function getNotificationChannelValue({ preferences, key, channel }: ChannelInput): boolean {
  return preferences?.[key]?.[channel] ?? defaultNotificationChannelEnabled(channel)
}

/** Turning off a channel under All Notifications disables that channel on every other row. */
export function isNotificationChannelDisabled({ preferences, key, channel }: ChannelInput): boolean {
  if (key === 'all') return false
  return !getNotificationChannelValue({ preferences, key: 'all', channel })
}

type FillDefaultsInput = {
  preferences: UserPreferences | undefined
  options: readonly NotificationPreferenceOption[]
}

/**
 * A save records the push-on / email-off values the screen is showing for each visible row.
 * `all` stays unset: a saved false disables that channel for every type, including rows this screen hides.
 */
export function fillNotificationPreferenceDefaults({ preferences, options }: FillDefaultsInput): UserPreferences {
  const filled: UserPreferences = { ...preferences }
  for (const { key } of options) {
    if (key === 'all') continue
    filled[key] = {
      ...preferences?.[key],
      push: getNotificationChannelValue({ preferences, key, channel: 'push' }),
      email: getNotificationChannelValue({ preferences, key, channel: 'email' }),
    }
  }
  return filled
}

/** The user update replaces `notificationPrefs` wholesale, so every other key must be carried over. */
export function setNotificationChannel({ preferences, key, channel, value }: SetChannelInput): UserPreferences {
  return {
    ...preferences,
    [key]: { ...preferences?.[key], [channel]: value },
  }
}
